import { objectFootprint } from '@shared-lib/geometry/objectFootprint';
import { isPointInsidePolygon } from '@shared-lib/geometry/pointInPolygon';

/**
 * Which horizontal surface is under a point in the floor plan.
 *
 * Placing a surface outlet means dropping it on whatever is there: a
 * countertop if the click lands on one, otherwise the floor. This answers the
 * first half of that — the slab and its top Y — and leaves the "otherwise the
 * floor" to the caller, which is the one that knows the room's floor height.
 *
 * WHY NOT `hostCountertopFor` (shared-lib/cabinet-runs/reparent.js): that is
 * the shared rule for RE-PARENTING — recovering which slab a sink belongs to
 * after a re-solve deleted the old one — so it deliberately falls back to the
 * nearest slab within 0.5 m. Recovering a lost association wants that
 * tolerance; reading a user's intent does not. With it, a click on open floor
 * up to half a metre from an island would seat the outlet at counter height,
 * in mid-air. So this test is STRICT containment, with no distance fallback.
 *
 * The two cannot disagree where it matters: `hostCountertopFor` returns
 * immediately on real coverage, so every point this accepts it accepts too.
 * They differ only in the 0-0.5 m band outside a slab, where this says "not on
 * a counter" — and the pipeline rules that use the tolerant version apply to
 * sinks, cooktops and ovens, never to devices.
 *
 * Rotation matters: countertops are routinely rotated (the sample island slab
 * in the reported room sits at -90 degrees), and the axis-aligned min/max box
 * test the other click-time countertop lookups in `useFloorplanGestures` use
 * is wrong for any slab off the grid. `objectFootprint` is the helper that
 * gets the handedness right — see its own header for the sign trap that makes
 * `calculateCorners(c, w, d, obj.rotation.y)` reflect the rectangle.
 *
 * Pure: no React, no three.js, no Firestore.
 */

/**
 * Object types that read as a horizontal surface something can sit ON.
 *
 * Both are real slabs in the data (dev carries 560 `countertop` and 2
 * `corner-fill-countertop`). Deliberately a small explicit set rather than a
 * name match: `upper-cabinet` and `fireplace` can carry a countertop but are
 * not themselves a surface to drop a device onto.
 */
export const SUPPORT_SURFACE_TYPES = new Set([
  'countertop',
  'corner-fill-countertop',
]);

/** @returns {boolean} True when this object is a droppable horizontal surface. */
export const isSupportSurface = (obj) =>
  SUPPORT_SURFACE_TYPES.has(String(obj?.type || '').toLowerCase());

/**
 * Is `(x, z)` inside this object's footprint, allowing `tolerance` metres of
 * slack on every side?
 *
 * THE containment rule for the floor plan's click-time lookups. It rotates the
 * point into the object's own frame and tests half-extents, which is the same
 * transform `hostCountertopFor` uses server-side, so the two agree about what
 * "on this slab" means.
 *
 * The handedness is the trap. A stored `rotation.y` puts local +X at
 * `(cos ry, -sin ry)` and local +Z at `(sin ry, cos ry)`, so the inverse — the
 * transform below — is `lx = dx·cos - dz·sin`, `lz = dx·sin + dz·cos`. Get the
 * sign wrong and the footprint is REFLECTED, which is free at 0/90/180/270 and
 * wrong everywhere else. `objectContainsPointXZ.test.js` pins it against
 * `objectFootprint`, the canonical helper, on an oblique non-square case.
 *
 * @param {Object} obj - Anything with `center`, `dimensions` and `rotation`
 * @param {number} x - World X
 * @param {number} z - World Z
 * @param {number} [tolerance=0] - Slack in metres on each side
 * @returns {boolean}
 */
export const objectContainsPointXZ = (obj, x, z, tolerance = 0) => {
  if (!obj?.center || !obj?.dimensions) return false;
  const dims = Array.isArray(obj.dimensions)
    ? { x: obj.dimensions[0], z: obj.dimensions[2] }
    : obj.dimensions;
  const halfX = (dims?.x || 0) / 2 + tolerance;
  const halfZ = (dims?.z || 0) / 2 + tolerance;
  if (halfX <= 0 || halfZ <= 0) return false;

  const dx = x - obj.center.x;
  const dz = z - obj.center.z;
  const ry = obj.rotation?.y || 0;
  const cos = Math.cos(ry);
  const sin = Math.sin(ry);
  const localX = dx * cos - dz * sin;
  const localZ = dx * sin + dz * cos;
  return Math.abs(localX) <= halfX && Math.abs(localZ) <= halfZ;
};

/**
 * The countertop under a point, or null. Shared by the sink-placement paths so
 * the preview and the click cannot disagree about which slab is under the
 * cursor.
 *
 * @param {number} x - World X
 * @param {number} z - World Z
 * @param {Array} countertops - Candidate slabs
 * @param {number} [tolerance=0.1] - Slack, so a click just off the edge lands
 * @returns {Object|null}
 */
export const countertopAtPoint = (x, z, countertops, tolerance = 0.1) =>
  (countertops || []).find((c) => objectContainsPointXZ(c, x, z, tolerance)) ||
  null;

/**
 * The top face of an object in world Y — where something resting on it sits.
 * Centers are stored world-absolute (a slab on an upper storey has a large Y),
 * so this needs no floor reference.
 *
 * @param {Object} obj
 * @returns {number|null} Top Y, or null when the object cannot supply one
 */
export const objectTopY = (obj) => {
  const centerY = obj?.center?.y;
  const height = obj?.dimensions?.y;
  if (!Number.isFinite(centerY) || !Number.isFinite(height)) return null;
  return centerY + height / 2;
};

/**
 * The highest support surface whose footprint covers `(x, z)`.
 *
 * Highest rather than first, so a slab overlapping another (an island lip over
 * a lower shelf) puts the device on the one you can actually see.
 *
 * @param {number} x - World X of the click
 * @param {number} z - World Z of the click (the floor plan's screen-down axis)
 * @param {Array} objects - Candidate objects, any types
 * @returns {{surface: Object, topY: number}|null} null when nothing covers it
 */
export const supportSurfaceAt = (x, z, objects) => {
  const point = { x, z };
  let best = null;
  for (const obj of objects || []) {
    if (!isSupportSurface(obj)) continue;
    const topY = objectTopY(obj);
    if (topY === null) continue;
    const footprint = objectFootprint(obj);
    if (!footprint || !isPointInsidePolygon(point, footprint)) continue;
    if (!best || topY > best.topY) best = { surface: obj, topY };
  }
  return best;
};
